import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  DriverSheet,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/Driver/DriverSheet'
import {
  EquipmentsSheet,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/Equipments/EquipmentsSheet'
import {
  carIsBeingUpgraded,
  carToBeUpgraded,
  editingCar,
  useCarRentalServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { removeCar, upgradeCar } from '@/scopes/checkout/domain/actions'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  mqMin,
  space,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Benefits, Button, Icon, IconGlyph, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import { sub } from 'date-fns'
import * as React from 'react'
import { css, styled } from 'styled-components'

const CarItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
`

export const CarItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${space('primitives_space_0')};
  border: 2px solid transparent;
  border-radius: ${tokPx('primitives_corner_radius_xl')};
  ${({ $selected }) => $selected && modSelected()}
`

const YourSelectionBadge = styled(Typography)`
  color: ${tok('semantics_content_white_color')};
  text-align: center;
  padding: ${space('primitives_space_6')};
`

function modSelected() {
  return css`
    background-color: ${tok('semantics_selectors_selected_background_color')};
    border-color: ${tok('primitives_color_selected_500')};

    ${CarItemContent} {
      background-color: ${tok('semantics_views_background_color')};
      margin-bottom: ${space('primitives_space_2')};
      margin-right: ${space('primitives_space_2')};
      margin-left: ${space('primitives_space_2')};
      border-radius: 8px;
      padding: ${space('primitives_space_6')};
    }
  `
}

export const CarRentalCard = styled.div`
  border: 2px solid ${tok('semantics_dividers_color')};
  border-radius: ${tokPx('semantics_cards_corner_radius')};
  background-color: ${tok('semantics_views_background_color')};
  display: grid;
  grid-template-areas:
    "image head"
    "info info"
    "footer footer";
  grid-template-rows: repeat(3, auto);
  grid-template-columns: 120px auto;
  width: 100%;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    grid-template-areas:
      "image head footer"
      "image info footer";
    grid-template-rows: repeat(2, auto);
    grid-template-columns: 160px auto 160px;
  }
`

export const CarImage = styled.img`
  width: 100%;
  height: auto;
  grid-area: image;
  border-radius: ${tokPx('semantics_cards_corner_radius')} 0 0 ${tokPx('semantics_cards_corner_radius')};
`
export const CarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_4')};
  flex: 1;
  grid-area: head;
  padding-top: ${space('primitives_space_12')};
  padding-bottom: ${space('primitives_space_6')};
`
const CarName = styled(Typography)`
  color: ${tok('semantics_content_color')};
`
export const CarInformationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
  border-top: 2px solid ${tok('semantics_dividers_color')};
  grid-area: info;
  padding-top: ${space('primitives_space_12')};
  padding-bottom: ${space('primitives_space_12')};
  width: 100%;
  flex: 1;
  padding: ${space('primitives_space_6')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding-left: ${space('primitives_space_0')};
    padding-right: ${space('primitives_space_0')};
    padding-top: ${space('primitives_space_8')};
    padding-bottom: ${space('primitives_space_8')};
`
const CarClass = styled(Typography)``

const CarFeatures = styled(Benefits.Root)`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${space('primitives_space_8')};
`

const LegalMenuBar = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  align-self: stretch;
  flex-wrap: wrap;
`

const LegalMenuBarSeparator = styled(Typography)`
align-self: center;
`

const CarRentalUsp = styled(Benefits.Item)`
  color: ${tok('semantics_content_color')};
  > div {
    align-items: center;
  }
  ${Icon} {
    ${Icon.modSize(20)}
  }
`
export const CarCardFooterSection = styled.div`
  padding: ${space('primitives_space_6')};
  width: 100%;
  grid-area: footer;
`

export const CarCardFooter = styled.div`
  background-color: ${tok('semantics_views_tinted_background_color')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: ${space('primitives_space_8')};
  border-radius: ${tokPx('primitives_corner_radius_s')};
  gap: ${space('primitives_space_6')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: ${space('primitives_space_12')};
    flex-direction: column;
    justify-content: center;
  }
`
export const Price = styled(Typography)`
  display: flex;
  align-items: center;
  color: ${tok('semantics_content_color')};
`

const Link = styled.a`
  align-items: center;
  display: flex;
  flex-direction: row;
  text-decoration: underline;
  color: ${tok('semantics_content_color')};
`

const OnRequest = styled(Typography)`
  text-decoration: underline;
  color: ${tok('semantics_content_color')};
  cursor: pointer;
`

export const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: ${space('primitives_space_6')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    ${Button} {
      width: 100%;
    }
  }
`

function CarOptionForms({ openDriverByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openDriverByDefault?: boolean }) {
  return (
    <>
      <DriverSheet {...option} openByDefault={openDriverByDefault} />
      <EquipmentsSheet {...option} />
    </>
  )
}

export function CarOption({ isSelected, openDriverByDefault = false, ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean, openDriverByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering)

  const intl = useIntl()

  if (!service)
    return null

  const { rooms } = service
  if (!rooms)
    return null

  const roomName = Object.values(rooms)
    .find(room => room.selectedValue === option.id)
    ?.name || ''

  return (
    <CarItem $selected={isSelected}>
      {isSelected && !option.isIncluded && <YourSelectionBadge $variant="body-s-bold-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_your_selection' })}</YourSelectionBadge>}
      {isSelected && option.isIncluded && <YourSelectionBadge $variant="body-s-bold-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_is_included_with_room' }, { roomName })}</YourSelectionBadge>}
      <CarItemContent>
        <CarOptionCardView isSelected={isSelected} {...option} />
        {isSelected && <CarOptionForms {...option} openDriverByDefault={openDriverByDefault} />}
      </CarItemContent>
    </CarItem>
  )
}

export function CarOptionCardView({ isSelected, ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean }) {
  const { id: carId, price, driverId, canBeRemoved, isIncluded, canBeUpgraded, equipments, selectedEquipments } = option
  const { sendEvent } = useAnalytics()
  const { isDesktopMd } = useBreakpoints()
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const { configurations } = service || {}

  const { driverId: currentDriverIdSelected } = option
  const driver = currentDriverIdSelected ? service?.participants[currentDriverIdSelected] : null

  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver, days: 1 })

  const { startEditingCar, endEditingCar, setCarIsBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, setCarToBeSelected } = useCarRentalServiceStore()
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded)
  const carValueToBeUpgraded = useCarRentalServiceStore(carToBeUpgraded)

  const intl = useIntl()

  const currentEditingCar = useCarRentalServiceStore(editingCar(carValueToBeUpgraded))

  const handleSelectClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return

    if (isCarBeingUpgraded) {
      if (currentEditingCar) {
        const {
          driverId: currentSelectedDriverId,
        } = currentEditingCar

        setServiceValue(upgradeCar({ value: carValue, participantId: currentSelectedDriverId ?? '' })(service))
      }

      setCarIsBeingUpgraded(false)
      setCarToBeUpgraded('')
      endEditingCar(carId)

      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: carValue,
          description: 'upgrade button clicked',
          element: 'button',
        }),
      )
    }

    setCarToBeSelected(carId)
    setShowExtraCars(false)

    if (!driverId && !currentEditingCar) {
      startEditingCar(carId, {
        driverId: null,
        driverBirthDate: defaultDriverBirthdate,
        driverHasCreditCard: false,
        bookedEquipmentsWithQuantity: [],
        isEditing: false,
      })
    }
  }, [currentEditingCar, endEditingCar, isCarBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, defaultDriverBirthdate, driverId, sendEvent, setCarToBeSelected, setCarIsBeingUpgraded, setServiceValue, startEditingCar, carId, service])

  const handleRemoveClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return
    if (!driverId) {
      setCarToBeSelected('')
    }
    if (driverId) {
      setServiceValue(removeCar({ participantId: driverId ?? '' })(service))
      sendEvent(
        createCarEvent({
          result: 'remove',
          type: carValue,
          description: 'remove button clicked',
          element: 'button',
        }),
      )
    }
  }, [sendEvent, driverId, setServiceValue, service])

  const handleChangeClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return
    setCarIsBeingUpgraded(true)
    setCarToBeUpgraded(carValue)

    if (!driver)
      return
    startEditingCar(
      carValue,
      {
        driverId,
        driverBirthDate: driver.birthdate ? new Date(driver.birthdate) : new Date(),
        driverHasCreditCard: true,
        bookedEquipmentsWithQuantity: equipments.filter(({ id }) => selectedEquipments?.includes(id)).map(({ id, quantitySelected }) => {
          return {
            id,
            quantity: quantitySelected ?? 1,
          }
        }),
        isEditing: true,
      },
    )

    sendEvent(
      createCarEvent({
        result: 'upgrade',
        type: carValue,
        description: 'upgrade button clicked',
        element: 'button',
      }),
    )
  }, [driver, driverId, equipments, selectedEquipments, setCarToBeUpgraded, startEditingCar, sendEvent, setCarIsBeingUpgraded, service])

  if (!service)
    return
  return (
    <CarRentalCard>
      <CarCardBody {...option} />
      <CarCardFooterSection>
        <CarCardFooter>
          <Price as="span" $variant="body-m-bold-tight">
            { isIncluded && intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_label' }) }
            { !isIncluded && intl.formatTruncatedCurrency(price) }
          </Price>
          <ActionButtons>
            {!isSelected && (
              <Button
                $type="secondary"
                onClick={() => handleSelectClick(carId)}
                $size="S"
              >
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text' })}
              </Button>
            )}
            {isSelected && (
              <>
                {canBeRemoved && !isIncluded && (
                  <Button
                    $type="secondary"
                    onClick={() => handleRemoveClick(carId)}
                    $size="S"
                  >
                    {isDesktopMd && <Icon size={16} glyph="Dismiss" /> }
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_remove_car_text' })}
                  </Button>
                )}
                {canBeUpgraded && (
                  <Button
                    $type="secondary"
                    onClick={() => handleChangeClick(carId)}
                    $size="S"
                  >
                    { isDesktopMd && isIncluded ? (<Icon size={16} glyph="ArrowUpFromLine" />) : (<Icon size={16} glyph="Pencil" />) }
                    { isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_car_text' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_change_car_text' })}
                  </Button>
                )}
              </>
            )}

          </ActionButtons>
        </CarCardFooter>
      </CarCardFooterSection>
    </CarRentalCard>
  )
}

export function CarCardBody({ ...option }: CheckoutCarRentalServiceOption | FilterableCarItem) {
  const { id, label, features, extraFeatures = [], conditionsUrl, image, carClass, availableOnRequest } = option
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const onRequestContent = intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_available_on_request_content' })

  const handleConditionsLinkClick = () => {
    sendEvent(
      createCarEvent({
        result: 'conditions page opened',
        type: id,
        description: 'Car conditions link clicked',
        element: 'button',
      }),
    )
  }

  return (
    <>
      {image && <CarImage src={image} alt={label} width={300} height={300} />}
      <CarHeader>
        <CarName $variant="display-h-regular">{label}</CarName>
        <CarClass $variant="body-s-regular-tight">{carClass}</CarClass>
        <CarFeatures $type="inline" $align="left">
          {features.map(({ glyph, text }) => (
            <CarRentalUsp
              key={glyph}
              title={text ?? ''}
              $icon={glyph as IconGlyph}
            />
          ))}
        </CarFeatures>
      </CarHeader>

      <CarInformationDetails>
        <CarFeatures $type="inline" $align="left">
          {extraFeatures.map(({ glyph, values }) => {
            const title = values?.map(text => intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_feature_${toSnakeCase(text ?? '')}_text`, defaultMessage: text ?? '' })).join(', ') ?? ''
            return (
              <CarRentalUsp
                key={glyph}
                title={title}
                $icon={glyph as IconGlyph}
              />
            )
          },
          )}
        </CarFeatures>
        <LegalMenuBar>
          <Link
            onClick={handleConditionsLinkClick}
            href={conditionsUrl}
            target="_blank"
            title={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text' })}
          >
            <Typography>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text' })}
            </Typography>
          </Link>
          {availableOnRequest && (
            <>
              <LegalMenuBarSeparator $variant="body-s-regular-tight">&nbsp;·&nbsp;</LegalMenuBarSeparator>
              <Sheet.Root>
                <Sheet.Trigger>
                  <OnRequest as="a">
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_available_on_request_label' })}
                  </OnRequest>
                </Sheet.Trigger>
                <Sheet.Body side="right" hasPadding>
                  <Sheet.Header
                    closeLabel={intl.formatMessage({ id: 'close' })}
                    titleLabel={intl.formatMessage({
                      id: 'checkout_steps_ancillaries_car_rental_available_on_request_title',
                    })}
                  />
                  <Sheet.Content>
                    <Typography dangerouslySetInnerHTML={{ __html: onRequestContent }} />
                  </Sheet.Content>
                </Sheet.Body>
              </Sheet.Root>
            </>
          )}
        </LegalMenuBar>
      </CarInformationDetails>
    </>
  )
}
